import {
  AUTH_TYPE,
  MODE,
} from "@/modules/transfer-money/transfer-by-account/types.d";
import {
  Card,
  Divider,
  Grid,
  Group,
  Radio,
  Select,
  Stack,
  Text,
  rem,
} from "@mantine/core";
import { formatCurrency } from "@/utils/common";
import { IconChevronDown } from "@tabler/icons-react";
import { FC, memo } from "react";
import { useTranslation } from "react-i18next";
import { ApprovalTransactionProps } from "./type";

const ApprovalTransaction: FC<ApprovalTransactionProps> = ({
  approvalInfo,
  ruleId: ruleIdProp,
  authority: authorityProp,
  authModes: authModesProp,
  amount,
  currentLevel,
  approvalStatus,
  approvalRefId,
  value,
  onChangeAuthType,
  canAuth = true,
}) => {
  const { t } = useTranslation();
  // Ưu tiên danh sách phương thức xác thực BE trả (value "1"=SMS, "2"=Smart OTP);
  // fallback list cứng khi không có prop (giữ tương thích các chỗ dùng cũ).
  const defaultAuthModes = [
    { value: AUTH_TYPE.SMS_OTP, label: "SMS OTP" },
    { value: AUTH_TYPE.MCODE_OTP, label: "SMART OTP" },
    { value: AUTH_TYPE.SIGNATURE, label: t("ELECTRONIC_SIGNATURE") },
  ];
  const authModes =
    authModesProp && authModesProp.length ? authModesProp : defaultAuthModes;
  // Giá trị đang chọn: ưu tiên value từ cha (controlled); nếu chưa có -> phần tử đầu.
  const authMode = value || authModes[0]?.value || AUTH_TYPE.SMS_OTP;
  // JVC-6989: CHO CHỌN phương thức xác thực khi đang TẠO (chưa có trạng thái) hoặc checker ĐANG
  // duyệt (PENDING/APPROVING); yêu cầu đã xử lý xong -> hiển thị text tĩnh, không cho chọn (design).
  const authStatusUpper = String(approvalStatus ?? "").toUpperCase();
  // JVC-6881: chỉ cho chọn PTXT khi user CÓ quyền hành động (duyệt/từ chối/hoàn lại). Không có
  // quyền -> hiển thị text tĩnh (đồng bộ với việc ẩn nút hành động). canAuth mặc định true để
  // không ảnh hưởng các màn khác (vd maker tạo GD).
  const isAuthEditable =
    canAuth &&
    (authStatusUpper === "" ||
      ["PENDING", "APPROVING"].includes(authStatusUpper));
  const authModeLabel =
    authModes.find((m) => String(m.value) === String(authMode))?.label ?? authMode;

  // Lấy data từ approvalInfo (ưu tiên), fallback về props cũ
  const ruleId =
    approvalInfo?.approvalProcessId ??
    approvalInfo?.lastApprovalLevelId ??
    ruleIdProp ??
    "";
  const authority =
    approvalInfo?.totalApprovalLevel ??
    approvalInfo?.numberOfApprovals ??
    approvalInfo?.lastApprovalLevel ??
    authorityProp ??
    "";

  // Bố cục chi tiết theo design "Thông tin phê duyệt" (dòng số tiền + trạng thái từng cấp) chỉ bật
  // khi bên gọi truyền currentLevel — các màn cũ giữ nguyên bố cục đang chạy.
  const detailed = currentLevel != null;

  // Ưu tiên nextLevelApprovers (có level info), fallback approvalUserNames
  // Group theo approvalLevel: cùng cấp hiển thị trên 1 dòng
  const approvers: { level: number; names: string }[] = (() => {
    if (approvalInfo?.nextLevelApprovers?.length) {
      const map = new Map<number, string[]>();
      [...approvalInfo.nextLevelApprovers]
        .sort((a: any, b: any) => Number(a.approvalLevel ?? 0) - Number(b.approvalLevel ?? 0))
        .forEach((a: any) => {
          const level = Number(a.approvalLevel ?? 1) || 1;
          const arr = map.get(level) || [];
          arr.push(a.approvalUserFullName);
          map.set(level, arr);
        });
      return Array.from(map.entries()).map(([level, names]) => ({
        level,
        names: names.join(", "),
      }));
    }
    if (approvalInfo?.approvalUserNames?.length) {
      return approvalInfo.approvalUserNames.map((names: string, i: number) => ({
        level: i + 1,
        names,
      }));
    }
    return [];
  })();

  /**
   * Trạng thái của một cấp so với cấp đang chờ xử lý.
   *
   * Chỉ gắn khi yêu cầu CÒN chờ duyệt: yêu cầu đã duyệt xong / từ chối thì "đang chờ" là sai sự
   * thật, và design cũng bỏ trống phần này.
   */
  const levelStateOf = (level: number): string => {
    const pending = ["PENDING", "APPROVING"].includes(
      String(approvalStatus ?? "").toUpperCase(),
    );
    if (!detailed || !pending || currentLevel == null) return "";
    if (level < currentLevel) return t("APPROVAL_LEVEL_DONE");
    if (level === currentLevel) return t("APPROVAL_LEVEL_WAITING");
    return t("APPROVAL_LEVEL_NOT_YET");
  };

  // Dòng đầu: "ID duyệt" = id của chính yêu cầu (design), CHỈ khi bên gọi truyền approvalRefId.
  // Không truyền thì giữ "Mã quy trình duyệt" = approvalProcessId như các màn đang chạy.
  // JVC-7004: KHÔNG ràng buộc `detailed` — màn chi tiết GD lô dùng bố cục non-detailed nhưng vẫn
  // cần nhãn "ID duyệt" (chỉ đổi nhãn+giá trị dòng đầu, không đổi phần còn lại của layout).
  const showApprovalRef =
    approvalRefId != null && String(approvalRefId) !== "";

  const amountText =
    amount == null || amount === "" ? "" : `${formatCurrency(amount)} VND`;
  // "1 cấp (tuần tự)" — Ma trận phê duyệt của doanh nghiệp duyệt lần lượt từ cấp thấp lên.
  const authorityText =
    detailed && authority !== "" ? `${authority} ${t("LEVEL_SEQUENTIAL")}` : authority;

  return (
    <Card shadow="sm" radius="md" pb={rem(30)}>
      <Card.Section inheritPadding py="xs">
        <Group position="apart">
          <Text fz="lg" weight={600}>
            {t("APPROVAL_INFORMATION")}
          </Text>
        </Group>
      </Card.Section>
      <Card.Section inheritPadding py="xs">
        <Stack spacing="md">
          {amountText && (
            <>
              <Grid>
                <Grid.Col span={6}>
                  <Text fw={600} size={14}>
                    {t("TRANSACTION_AMOUNT")}
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text fw={600} ta="right" size={16} c="#2A81D0">
                    {amountText}
                  </Text>
                </Grid.Col>
              </Grid>
              <Divider variant="dashed" my={0} />
            </>
          )}
          <Grid>
            <Grid.Col span={6}>
              <Text color="#5E656B" size={14}>
                {showApprovalRef ? t("APPROVAL_ID") : t("RULE_ID")}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text fw={500} ta="right" size={14}>
                {showApprovalRef ? String(approvalRefId) : ruleId}
              </Text>
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={6}>
              <Text color="#5E656B" size={14}>
                {t("AUTHORITY")}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text fw={500} ta="right" size={14}>
                {authorityText}
              </Text>
            </Grid.Col>
          </Grid>
          {detailed && approvers.length > 0 && (
            <Divider variant="dashed" my={0} />
          )}
          {approvers.map(({ level, names }) => {
            const state = levelStateOf(level);
            return (
              <Grid key={`approver-${level}-${names}`} gutter="md" align="center">
                <Grid.Col span={6}>
                  <Text color="#5E656B" size={14}>
                    {detailed ? t("LEVEL") : t("APPROVER_LEVEL")} {level}
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text fw={500} ta="right" size={14} c="#2A81D0">
                    {names}
                    {state && (
                      <Text component="span" fw={500} size={13} c="#5E656B">
                        {` (${state})`}
                      </Text>
                    )}
                  </Text>
                </Grid.Col>
              </Grid>
            );
          })}
        </Stack>
      </Card.Section>
      <Divider my="sm" variant="dashed" />
      <Card.Section inheritPadding py="xs">
        {isAuthEditable ? (
          <>
            <Select
              label={`${t("AUTHENTICATION_MODE")}`}
              styles={{ label: { fontWeight: 600, fontSize: "16px" } }}
              value={authMode}
              onChange={(v) => onChangeAuthType?.(v || "")}
              data={authModes}
              mb="sm"
              rightSection={<IconChevronDown size="1rem" />}
            />
            {["2", "3"].includes(String(authMode)) && (
              <Radio.Group
                styles={{ label: { fontSize: "18px", fontWeight: 600 } }}
                label={t("MODE")}
              >
                <Group mt="xs">
                  <Radio value={MODE.SMARTOTP_PUSH} label={t("SMARTOTP_PUSH")} />
                  <Radio value={MODE.SMARTOTP_CODE} label={t("SMARTOTP_CODE")} />
                </Group>
              </Radio.Group>
            )}
          </>
        ) : (
          // JVC-6989: yêu cầu đã xử lý -> PTXT chỉ hiển thị (text tĩnh), không cho chọn.
          <Group position="apart" align="center">
            <Text fw={600} size={16} color="#5E656B">
              {t("AUTHENTICATION_MODE")}
            </Text>
            <Text fw={500} size={14}>
              {authModeLabel}
            </Text>
          </Group>
        )}
      </Card.Section>
    </Card>
  );
};

export default memo(ApprovalTransaction);
